import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";

const COLORS = ["#DF513B", "#1A1714", "#E8B241", "#4F7355", "#645E58"];

const ChartCard = ({ title, subtitle, children, testId }) => (
  <div className="brutal-card p-5 md:p-6" data-testid={testId}>
    <div className="flex items-baseline justify-between mb-3 border-b-2 border-dotr-ink pb-3">
      <h3 className="font-heading font-semibold text-xl tracking-tight uppercase">{title}</h3>
      {subtitle && <span className="font-mono text-[11px] uppercase tracking-widest text-dotr-inkmuted">{subtitle}</span>}
    </div>
    <div className="h-[260px]">{children}</div>
  </div>
);

const tooltipStyle = {
  border: "2px solid #1A1714",
  borderRadius: 0,
  background: "#FFFFFF",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 12,
  boxShadow: "4px 4px 0 #1A1714",
};

export const StatsCharts = ({ stats }) => {
  if (!stats) return null;
  const byLocation = stats.by_location || [];
  const mealSplit = stats.meal_split || [];
  const byDay = stats.by_day || [];
  const topItems = stats.top_items || [];

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
      <ChartCard title="Orders by Location" subtitle="Bar" testId="chart-by-location">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={byLocation} margin={{ top: 8, right: 12, left: -20, bottom: 4 }}>
            <CartesianGrid stroke="#1A1714" strokeDasharray="2 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(223,81,59,0.08)" }} />
            <Bar dataKey="value" fill="#DF513B" stroke="#1A1714" strokeWidth={1.5} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Lunch vs Dinner" subtitle="Split" testId="chart-meal-split">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={mealSplit}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              stroke="#1A1714"
              strokeWidth={2}
              isAnimationActive
              label={(e) => `${e.name}: ${e.value}`}
              labelLine={false}
            >
              {mealSplit.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            <Legend
              wrapperStyle={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, textTransform: "uppercase" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Orders Trend" subtitle="Last 14d" testId="chart-trend">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={byDay} margin={{ top: 8, right: 12, left: -20, bottom: 4 }}>
            <CartesianGrid stroke="#1A1714" strokeDasharray="2 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} tickFormatter={(d) => (d || "").slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Line type="stepAfter" dataKey="orders" stroke="#1A1714" strokeWidth={2.5} dot={{ r: 4, fill: "#DF513B", stroke: "#1A1714", strokeWidth: 1.5 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>

    {topItems.length > 0 && (
      <div className="mb-6">
        <ChartCard title="Top Items (by qty)" subtitle="All time" testId="chart-top-items">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={topItems} margin={{ top: 8, right: 20, left: 80, bottom: 4 }}>
              <CartesianGrid stroke="#1A1714" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fill: "#1A1714" }} axisLine={{ stroke: "#1A1714" }} tickLine={false} width={140} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(223,81,59,0.08)" }} />
              <Bar dataKey="quantity" fill="#E8B241" stroke="#1A1714" strokeWidth={1.5} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    )}
    </>
  );
};
